import { MarketingShell } from '../../components/MarketingShell'
import { Seo } from '../../components/Seo'
import { ButtonLink } from '../../components/ui/Button'
import { WaitlistCta } from '../../components/marketing/WaitlistForm'

const providers = [
  {
    name: 'Xero',
    body: "Sales invoices, contacts and payment status. The amount chased is the invoice's amount due, so credit already applied in Xero is not chased.",
    status: 'Live',
  },
  {
    name: 'QuickBooks',
    body: "QuickBooks Online sales invoices, including part payments. Reminders read the invoice's own due date and terms rather than a schedule you have to maintain twice.",
    status: 'Live',
  },
  {
    name: 'Sage',
    body: 'Sage Accounting (South Africa). In development; not yet available.',
    status: 'Coming soon',
  },
]

const dataAccessRows = [
  { data: 'Sales invoices and due dates', access: 'Read', why: 'To know what is outstanding and when' },
  { data: 'Customer name and contact number', access: 'Read', why: 'To send the reminder to the right person' },
  { data: 'Payments and amount due', access: 'Read', why: 'To check before every message that it is still unpaid, and to chase the balance rather than the gross' },
    { data: 'Ledger entries, journals, payroll', access: 'None', why: 'Not needed, not requested' },
  { data: 'Anything written back to your books', access: 'None', why: 'RemindLoop cannot edit your accounts' },
]

// See Home.tsx's matching comment for why these are exported.
export const seoTitle = 'Integrations — Xero & QuickBooks (Sage Coming Soon)'
export const seoDescription =
  "RemindLoop reads directly from Xero or QuickBooks: what's outstanding, who to contact, and whether it has been paid before each reminder. See exactly what it reads, and what it never touches. Sage support is coming soon."

export default function Integrations() {
  return (
    <MarketingShell>
      <Seo title={seoTitle} description={seoDescription} path="/integrations" />

      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] pt-[clamp(44px,7vw,88px)] pb-[clamp(24px,3vw,36px)]">
        <h1 className="max-w-[20ch] text-[clamp(30px,4.4vw,48px)] leading-[1.1] font-semibold tracking-[-0.03em]">
          Your ledger is the source of truth
        </h1>
        <p className="mt-4.5 max-w-[58ch] text-[clamp(16px,1.5vw,18px)] leading-[1.6] text-foreground/76">
          RemindLoop never asks you to re-enter an invoice. It signs in to your accounting system,
          reads what is outstanding, and checks for payment before every message so it knows when to stop.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] py-[clamp(28px,4vw,48px)]">
        <div className="grid grid-cols-1 gap-x-[clamp(28px,4vw,64px)] gap-y-8 sm:grid-cols-3">
          {providers.map((provider) => (
            <div key={provider.name}>
              <h2 className="text-[22px] leading-[1.2] font-semibold">{provider.name}</h2>
              <p className="mt-3 text-body text-foreground/78">{provider.body}</p>
              <span
                className={
                  provider.status === 'Live'
                    ? 'mt-3 inline-block rounded-sm bg-brand-200 px-2 py-1 text-tag tracking-[0.06em] text-brand-700 uppercase dark:bg-brand-900 dark:text-brand-400'
                    : 'mt-3 inline-block rounded-sm bg-muted px-2 py-1 text-tag tracking-[0.06em] text-muted-foreground uppercase'
                }
              >
                {provider.status}
              </span>
            </div>
          ))}
        </div>
        {/* Added 2026-10-08 (user request: "a user should not have to
            scroll far to get to a call to action link") -- this page's
            only CTA used to be "Request an integration" at the very
            bottom, aimed at people on neither Xero, QuickBooks nor Sage.
            Everyone else reading this far had nothing to click. */}
        <div className="mt-10 text-center">
          <WaitlistCta
            to="/register"
            source="integrations"
            className="min-h-11 bg-magenta-700 px-6 text-base text-white hover:bg-magenta-800"
          >
            Join the waitlist
          </WaitlistCta>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] py-[clamp(28px,4vw,48px)]">
        <h2 className="mb-6 text-[clamp(24px,2.8vw,32px)] leading-[1.2] font-semibold tracking-[-0.015em]">
          Exactly what we read, and what we never touch
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[560px] border-collapse text-left text-[14px]">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Access</th>
                <th className="px-4 py-3 font-semibold">Why</th>
              </tr>
            </thead>
            <tbody>
              {dataAccessRows.map((row) => (
                <tr key={row.data} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">{row.data}</td>
                  <td className="px-4 py-3">{row.access}</td>
                  <td className="px-4 py-3 text-foreground/76">{row.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] py-[clamp(40px,6vw,72px)]">
        <h2 className="max-w-[24ch] text-[clamp(24px,2.8vw,32px)] leading-[1.2] font-semibold tracking-[-0.015em]">
          On a different system?
        </h2>
        <p className="mt-3.5 max-w-[56ch] text-body text-foreground/78">
          FreeAgent, Zoho Books, NetSuite, or your own billing platform — tell us which and we will
          say honestly how soon, or point you at a CSV route in the meantime.
        </p>
        <div className="mt-5">
          <ButtonLink
            to="/contact"
            className="min-h-11 bg-magenta-700 px-6 text-base text-white hover:bg-magenta-800"
          >
            Request an integration
          </ButtonLink>
        </div>
      </section>
    </MarketingShell>
  )
}
